import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import focoLogo from '@/assets/foco-ai-logo.png';

/**
 * Compatibilidad: los enlaces antiguos usaban ?token= y una Edge Function.
 * La verificación nueva ocurre al abrir el enlace del correo de Supabase (aterrizaje en /dashboard).
 */
export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const legacyToken = searchParams.get('token');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (legacyToken) {
      toast({
        title: 'Enlace antiguo',
        description: 'El sistema de verificación cambió. Usa el botón de abajo o revisa el último correo de Supabase.',
        variant: 'destructive',
      });
    }
  }, [legacyToken]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = resendEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast({ title: 'Email inválido', variant: 'destructive' });
      return;
    }
    setResendLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });
      if (error) throw error;
      toast({
        title: 'Correo enviado',
        description: 'Si hay una cuenta pendiente de confirmar, recibirás un nuevo enlace.',
      });
    } catch (err: unknown) {
      console.error('resend signup:', err);
      toast({
        title: 'No se pudo reenviar',
        description: err instanceof Error ? err.message : 'Intenta de nuevo más tarde.',
        variant: 'destructive',
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-8">
      <div className="w-full max-w-md space-y-6 sm:space-y-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <img src={focoLogo} alt="FOCO AI System" className="h-14 sm:h-16 w-auto" />
        </div>

        <div className="mx-auto w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
          <Mail className="h-8 w-8 text-accent" />
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold">Verificación de correo</h1>

        {legacyToken ? (
          <p className="text-muted-foreground text-sm sm:text-base">
            Este enlace con código ya no es válido. Confirma tu cuenta con el enlace del último correo que te envió
            <strong> Supabase</strong>, o solicita uno nuevo aquí.
          </p>
        ) : (
          <p className="text-muted-foreground text-sm sm:text-base">
            Tras registrarte, abre el enlace del correo de confirmación (enviado por Supabase). Serás redirigido al
            dashboard y tu sesión quedará activa.
          </p>
        )}

        <form onSubmit={handleResend} className="space-y-3 text-left">
          <label htmlFor="resend-email" className="text-sm font-medium">
            Reenviar correo de confirmación
          </label>
          <Input
            id="resend-email"
            type="email"
            autoComplete="email"
            placeholder="tu@email.com"
            value={resendEmail}
            onChange={(e) => setResendEmail(e.target.value)}
            className="h-12"
          />
          <Button type="submit" className="w-full h-12" disabled={resendLoading}>
            {resendLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando…
              </>
            ) : (
              'Enviar de nuevo'
            )}
          </Button>
        </form>

        <Button variant="outline" className="w-full h-12" onClick={() => navigate('/auth')}>
          Ir a iniciar sesión
        </Button>
      </div>
    </div>
  );
}
